import { useCallback, useEffect, useRef } from "react";
import {
    useDataChannel,
    useLocalParticipant,
    useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";

import { BLS_TOPIC, BlsMessage, parseBlsMessage } from "../domain/blsChannel";
import { useBlsSession } from "./useBlsSession";
import type { BlsChannel } from "./useBlsSession";

export type { BlsChannel } from "./useBlsSession";

const encoder = new TextEncoder();

type DataSend = (payload: Uint8Array, options: object) => Promise<void> | void;

/**
 * Bilateral stimulation over the call's LiveKit data channel.
 *
 * A thin transport around `useBlsSession`. Call it above whatever shows the
 * stimulus, so the panel coming and going does not end a set that is running.
 */
export function useBlsChannel(): BlsChannel {
    const room = useRoomContext();
    const { localParticipant } = useLocalParticipant();
    /*
     * Read live from the room, never from a render: the identity arrives with
     * the connection, and nothing guarantees a render in between — a value
     * captured at mount stays empty, and an empty identity is neither the
     * therapist nor the patient.
     */
    const localParticipantRef = useRef(localParticipant);
    localParticipantRef.current = localParticipant;
    const readIdentity = useCallback(
        () => room?.localParticipant?.identity || localParticipantRef.current?.identity || "",
        [room]
    );

    const dataSendRef = useRef<DataSend | null>(null);
    const roomRef = useRef(room);
    useEffect(() => {
        roomRef.current = room;
    }, [room]);

    const send = useCallback((message: BlsMessage) => {
        const dataSend = dataSendRef.current;
        if (!dataSend) return;
        /*
         * Nothing goes out before the room is connected. LiveKit caches the
         * first attempt to open its data transport, and an attempt made while
         * the call is still connecting fails and stays failed: every packet
         * after it — this tool's, the whiteboard's, the meditation's — is
         * rejected for the rest of the call. That is how a set the therapist
         * started never reached the patient. Whatever is skipped here is sent
         * by `announce` once the room is connected.
         */
        if (roomRef.current?.state !== ConnectionState.Connected) return;
        return dataSend(encoder.encode(JSON.stringify(message)), { reliable: true, topic: BLS_TOPIC });
    }, []);

    const session = useBlsSession(send, readIdentity);
    const { receive, announce, peerLeft } = session;

    const onMessage = useCallback(
        (raw: { payload: Uint8Array; from?: { identity: string } }) => {
            const message = parseBlsMessage(raw.payload);
            if (message) receive(message, raw.from?.identity ?? "");
        },
        [receive]
    );

    const { send: dataSend } = useDataChannel(BLS_TOPIC, onMessage);
    // Declared before the effect below, so on mount inside a room that is
    // already connected the sender is in place when it announces.
    useEffect(() => {
        dataSendRef.current = dataSend as unknown as DataSend;
    }, [dataSend]);

    /**
     * Put both sides in step once the room is connected and says who we are.
     * Registered from the start and re-checked on every connection change,
     * reading the identity live, so neither the order of the two nor the
     * timing of React's renders can make it miss.
     */
    useEffect(() => {
        if (!room) return;
        const announceWhenReady = () => {
            if (room.state === ConnectionState.Connected && readIdentity()) announce();
        };
        announceWhenReady();
        room.on(RoomEvent.Connected, announceWhenReady);
        room.on(RoomEvent.ConnectionStateChanged, announceWhenReady);
        room.on(RoomEvent.Reconnected, announceWhenReady);
        return () => {
            room.off(RoomEvent.Connected, announceWhenReady);
            room.off(RoomEvent.ConnectionStateChanged, announceWhenReady);
            room.off(RoomEvent.Reconnected, announceWhenReady);
        };
    }, [announce, readIdentity, room]);

    useEffect(() => {
        if (!room) return;
        const onLeft = (participant: { identity: string }) => peerLeft(participant.identity);
        room.on(RoomEvent.ParticipantDisconnected, onLeft);
        return () => {
            room.off(RoomEvent.ParticipantDisconnected, onLeft);
        };
    }, [peerLeft, room]);

    return session;
}
